import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Box,
  Divider,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  FormHelperText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiService } from "../components/ReasuableFuncations/api";

const StakeholderDashboard = () => {
  const [stakeholders, setStakeholders] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [stakeholderToDelete, setStakeholderToDelete] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    type: "",
    role: "",
    selectedOption: "",
    regionalNetworkOption: "",
    memberStateOption: "",
    countryOption: "",
    contactInfo: {
      phone: "",
      email: "",
      address: "",
    },
  });

  const typeOptions = [
   "Member State",
    "Public Sector",
    "Public - Private Partnership",
    "Development Partner",
  ];

  useEffect(() => {
    fetchStakeholders();
  }, []);

  const fetchStakeholders = async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      setError("User data not found");
      return;
    }

    const {
      selectedOption,
      regionalNetworkOption,
      memberStateOption,
      countryOption,
    } = userData;

    let url = '/api/stakeholders';
    const params = new URLSearchParams();

    try {
      let response;
      const queryString = params.toString();
      
      if (selectedOption === "Comesa") {
        response = await apiService.get(`${url}${queryString ? `?${queryString}` : ''}`);
      } else if (selectedOption === "Regional Network" && regionalNetworkOption) {
        response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}&${queryString}`);
      } else if (selectedOption === "Member State" && memberStateOption) {
        response = await apiService.get(`${url}?country=${countryOption}&${queryString}`);
      }

      if (response.statusText !== "OK") {
        throw new Error("Failed to fetch stakeholders");
      }

      setStakeholders(response.data);
    } catch (error) {
      console.error("Error fetching stakeholders:", error);
      setError("Failed to fetch stakeholders. Please try again later.");
    }
  };

  const handleDelete = async () => {
    if (stakeholderToDelete) {
      try {
        const response = await apiService.delete(`/api/stakeholders/${stakeholderToDelete}`);
        if (response.statusText === "OK") {
          setStakeholders(stakeholders.filter(s => s._id !== stakeholderToDelete));
          setSnackbar({
            open: true,
            message: "Stakeholder deleted successfully!",
            severity: "success",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Error deleting stakeholder",
          severity: "error",
        });
      }
      setOpenDeleteDialog(false);
      setStakeholderToDelete(null);
    }
  };

  const handleEdit = (stakeholder) => {
    setEditFormData({
      name: stakeholder.name,
      type: stakeholder.type,
      role: stakeholder.role,
      selectedOption: stakeholder.selectedOption,
      regionalNetworkOption: stakeholder.regionalNetworkOption,
      memberStateOption: stakeholder.memberStateOption,
      countryOption: stakeholder.countryOption,
      contactInfo: {
        phone: stakeholder.contactInfo.phone,
        email: stakeholder.contactInfo.email,
        address: stakeholder.contactInfo.address,
      },
    });
    setSelectedStakeholder(stakeholder);
    setOpenDialog(true);
  };

  const handleUpdate = async () => {
    try {
      const response = await apiService.put(
        `/api/stakeholders/${selectedStakeholder._id}`,
        editFormData
      );

      if (response.statusText === "OK") {
        setStakeholders(stakeholders.map(s =>
          s._id === selectedStakeholder._id ? { ...s, ...editFormData } : s
        ));
        setSnackbar({
          open: true,
          message: "Stakeholder updated successfully!",
          severity: "success",
        });
        setOpenDialog(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error updating stakeholder",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <h1 style={{ textAlign: "center", marginBottom: "1rem" }}>
        Stakeholder Dashboard
      </h1>
      <Divider sx={{ mb: 2 }} />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Country</TableCell>
              {/* <TableCell>Actions</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {stakeholders.map((stakeholder) => (
              <TableRow key={stakeholder._id}>
                <TableCell>{stakeholder.name}</TableCell>
                <TableCell>{stakeholder.type}</TableCell>
                <TableCell>{stakeholder.role}</TableCell>
                <TableCell>{stakeholder.contactInfo.email}</TableCell>
                <TableCell>{stakeholder.contactInfo.phone}</TableCell>
                <TableCell>{stakeholder.regionalNetworkOption}</TableCell>
                <TableCell>{stakeholder.countryOption}</TableCell>
                {/* <TableCell>
                  <IconButton onClick={() => handleEdit(stakeholder)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setStakeholderToDelete(stakeholder._id);
                      setOpenDeleteDialog(true);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Stakeholder</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={editFormData.name}
            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth>
            <Autocomplete
              value={editFormData.type}
              onChange={(event, newValue) => 
                setEditFormData({ ...editFormData, type: newValue })
              }
              options={typeOptions}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stakeholder Type"
                  variant="outlined"
                  required
                />
              )}
            />
          </FormControl>
          <TextField
            fullWidth
            label="Role"
            value={editFormData.role}
            onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            value={editFormData.contactInfo.email}
            onChange={(e) => setEditFormData({
              ...editFormData,
              contactInfo: { ...editFormData.contactInfo, email: e.target.value }
            })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            value={editFormData.contactInfo.phone}
            onChange={(e) => setEditFormData({
              ...editFormData,
              contactInfo: { ...editFormData.contactInfo, phone: e.target.value }
            })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Address"
            value={editFormData.contactInfo.address}
            onChange={(e) => setEditFormData({
              ...editFormData,
              contactInfo: { ...editFormData.contactInfo, address: e.target.value }
            })}
            margin="normal"
          />
          {/* Add more form fields as needed */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this stakeholder?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StakeholderDashboard;
